import { useMemo } from 'react'
import { Box } from '@mui/material'
import Plot from 'react-plotly.js'
import {
  RowSelection,
  SiteWalk,
  WaterfallRow,
  selectRows,
  sharedRange,
  walk,
} from './stockWaterfall.walk'
import { buildFigure, parsePlanStart } from './stockWaterfall.figure'

const CONFIG = { displaylogo: false, responsive: true }

// The walk for the page's rows, site filter and week range; shared by the
// graphs and the legend.
export const useStockWaterfallWalk = (
  rows: WaterfallRow[] | undefined,
  { sites, startDateKey, endDateKey }: RowSelection
): SiteWalk[] =>
  useMemo(
    () =>
      rows ? walk(selectRows(rows, { sites, startDateKey, endDateKey })) : [],
    [rows, sites, startDateKey, endDateKey]
  )

type StockWaterfallGraphsProps = {
  sites: SiteWalk[]
  planStartWeek?: string | null
  onSiteHover?: (site: string | null) => void
}

// One small multiple per replenishment site, one shared y-range.
export const StockWaterfallGraphs = ({
  sites,
  planStartWeek,
  onSiteHover,
}: StockWaterfallGraphsProps) => {
  const range = useMemo(() => sharedRange(sites), [sites])
  const planStart = useMemo(
    () => parsePlanStart(planStartWeek),
    [planStartWeek]
  )
  const oneGraph = sites.length === 1
  return (
    <>
      {sites.map((site, index) => {
        const { data, layout } = buildFigure(site, {
          range,
          height: oneGraph ? 300 : 200,
          planStart,
        })
        return (
          <Box
            key={`spe-waterfall-${site.site}`}
            data-site-code={site.site}
            height={oneGraph ? '302px' : '202px'}
            mb={sites.length - 1 === index ? 0 : 0.25}
            onMouseEnter={() => onSiteHover?.(site.site)}
            onMouseLeave={() => onSiteHover?.(null)}
          >
            <Plot
              data={data}
              layout={layout}
              config={CONFIG}
              useResizeHandler
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
        )
      })}
    </>
  )
}
